import axios from 'axios';

// Determine the API base URL based on environment
const getApiBaseUrl = () => {
  // Check if we're in production by looking at the current URL
  const isProduction = window.location.hostname !== 'localhost' && 
                      window.location.hostname !== '127.0.0.1' &&
                      !window.location.hostname.includes('localhost');
  
  // Force localhost for development
  if (!isProduction) {
    console.log('🔧 Using localhost for development');
    return 'http://localhost:5001';
  }
  
  // Only use production URL if explicitly set
  if (process.env.REACT_APP_API_URL) {
    console.log('🔧 Using REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // Production fallback
  console.log('🔧 Using production URL');
  return 'https://sabiwabeeai-production.up.railway.app';
};

const API_URL = getApiBaseUrl();

// Create axios instance with better mobile support
const journalAxios = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 60000, // Increased timeout for mobile networks
  headers: {
    'Content-Type': 'application/json',
    'X-Device-Type': 'mobile',
  },
});

// Add request interceptor for auth
journalAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Journal API service
export const journalApi = {
  // Get all journal entries
  getEntries: async () => {
    try {
      const response = await journalAxios.get('/journal/entries');
      return response.data;
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      throw error;
    }
  },

  // Get a specific journal entry
  getEntry: async (id) => {
    try {
      const response = await journalAxios.get(`/journal/entries/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching journal entry:', error);
      throw error;
    }
  },

  // Create a new journal entry
  createEntry: async (entryData) => {
    try {
      const response = await journalAxios.post('/journal/entries', entryData);
      return response.data;
    } catch (error) {
      console.error('Error creating journal entry:', error);
      throw error;
    }
  },

  // Update a journal entry
  updateEntry: async (id, entryData) => {
    try {
      const response = await journalAxios.put(`/journal/entries/${id}`, entryData);
      return response.data;
    } catch (error) {
      console.error('Error updating journal entry:', error);
      throw error;
    }
  },

  // Delete a journal entry
  deleteEntry: async (id) => {
    try {
      const response = await journalAxios.delete(`/journal/entries/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      throw error;
    }
  },

  // Generate AI insights for a journal entry
  generateInsights: async (entryId) => {
    try {
      const response = await journalAxios.post(`/journal/entries/${entryId}/insights`);
      return response.data;
    } catch (error) {
      console.error('Error generating AI insights:', error);
      throw error;
    }
  },

  // Get AI insights for a journal entry
  getInsights: async (entryId) => {
    try {
      const response = await journalAxios.get(`/journal/entries/${entryId}/insights`);
      return response.data;
    } catch (error) {
      console.error('Error fetching journal insights:', error);
      throw error;
    }
  },

  // Get all insights for the user
  getAllInsights: async () => {
    try {
      const response = await journalAxios.get('/journal/insights');
      return response.data;
    } catch (error) {
      console.error('Error fetching all insights:', error);
      throw error;
    }
  }
}; 